import {
  convertWorkSizeToString,
  FormationData,
  FormationItemData,
  getDefaultSizeData,
  PEWorkData,
  SelectedData,
  WorkDetailData,
  WorkDetailSimpleData,
  WorkSimpleWithDetails,
} from '../dto/EntityRepository';
import { UpdateFormationItemReq } from '../dto/ReqDtoRepository';
import {
  DetailType,
  DisplaySize,
  DimensionsType,
  FormationHorizontalAlignment,
  FormationImageMode,
  FormationType,
  FormationVerticalAlignment,
  WorkType,
  usesWorkDuration,
} from '../enum/EnumRepository';
import { resolveWorkArtistName } from './workArtist';

const FOCUS_FACTOR = 1000;
const PENDING_FORMATION_ITEM_ID_PREFIX = 'pending-formation-item-';
const ASPECT_RATIO_EPSILON = 0.001;

export type FormationFitAlignmentAxis = 'horizontal' | 'vertical' | 'none';
export type FormationItemSource = 'work' | 'detail' | 'directUpload' | 'empty';

export interface FormationContainImageLayout {
  scaledWidth: number;
  scaledHeight: number;
  whitespaceX: number;
  whitespaceY: number;
}

export interface ResolvedFormationSlotImage {
  originUrl: string;
  width: number;
  height: number;
  focusX: number;
  focusY: number;
  alt: string;
}

export function getFormationItemSource(item: FormationItemData): FormationItemSource {
  if (item.work) return 'work';
  if (item.workDetail) return 'detail';
  if (item.uploadFile.originUrl) return 'directUpload';
  return 'empty';
}

const normalizeFormationInfoPart = (value: unknown): string => {
  if (typeof value !== 'string' && typeof value !== 'number') return '';
  const normalized = String(value).trim();
  return normalized === '-' || normalized === '—' ? '' : normalized;
};

export function formatFormationItemInfo(
  item: FormationItemData,
  includeArtist = false,
): string {
  const source = getFormationItemSource(item);

  if (source === 'work' && item.work) {
    const sizeOrDuration = usesWorkDuration(item.work.workType)
      ? item.work.duration
      : convertWorkSizeToString(
          item.work.size,
          item.work.dimensionsType,
          item.work.sizeText,
        );
    const parts = [
      includeArtist ? resolveWorkArtistName(item.work.artist) : '',
      item.work.title,
      item.work.prodYear,
      item.work.material,
      sizeOrDuration,
    ]
      .map(normalizeFormationInfoPart)
      .filter(Boolean);
    return parts.join(', ');
  }

  if (source === 'detail') {
    return (item.workDetail?.description ?? '').trim();
  }

  if (source === 'directUpload') {
    return item.caption?.trim() ?? '';
  }

  return '';
}

/** 백엔드 FormationItem.hasDirectUpload / snapshot 우선순위와 동일 */
export function resolveFormationSlotImage(
  item: FormationItemData,
): ResolvedFormationSlotImage | null {
  if (item.work?.uploadFile?.originUrl) {
    return {
      originUrl: item.work.uploadFile.originUrl,
      width: item.work.uploadFile.width,
      height: item.work.uploadFile.height,
      focusX: item.focusX,
      focusY: item.focusY,
      alt: item.work.title,
    };
  }

  if (item.workDetail?.uploadFile?.originUrl) {
    return {
      originUrl: item.workDetail.uploadFile.originUrl,
      width: item.workDetail.uploadFile.width,
      height: item.workDetail.uploadFile.height,
      focusX: item.focusX,
      focusY: item.focusY,
      alt: item.workDetail.description,
    };
  }

  if (item.uploadFile.originUrl) {
    return {
      originUrl: item.uploadFile.originUrl,
      width: item.uploadFile.width,
      height: item.uploadFile.height,
      focusX: item.focusX,
      focusY: item.focusY,
      alt: 'Formation slot',
    };
  }

  return null;
}

export type NormalizedFormationType = Exclude<
  FormationType,
  FormationType.FEATURE_TOP
>;

const FORMATION_TYPE_LABELS: Record<NormalizedFormationType, string> = {
  [FormationType.TWO_COLUMNS]: '2 Columns',
  [FormationType.FEATURE_SIDE]: 'Feature Side',
  [FormationType.THREE_COLUMNS]: '3 Columns',
};

export function normalizeFormationType(
  formationType: FormationType,
): NormalizedFormationType {
  return formationType === FormationType.FEATURE_TOP
    ? FormationType.FEATURE_SIDE
    : formationType;
}

export function normalizeFormationData(formation: FormationData): FormationData {
  return {
    ...formation,
    id: String(formation.id),
    formationType: normalizeFormationType(formation.formationType),
    items: formation.items.map((item) => ({
      ...item,
      id: String(item.id),
      caption: item.caption ?? '',
    })),
  };
}

export function getFormationTypeLabel(formationType: FormationType): string {
  return FORMATION_TYPE_LABELS[normalizeFormationType(formationType)] ?? 'Formation';
}

export function normalizeFormationDisplaySize(displaySize: DisplaySize): DisplaySize {
  switch (displaySize) {
    case DisplaySize.WIDE:
    case DisplaySize.BIG:
    case DisplaySize.MEDIUM:
      return displaySize;
    default:
      return DisplaySize.BIG;
  }
}

function normalizeFormationFocus(value: number): number {
  const clamped = Math.min(1, Math.max(0, value));
  return Math.trunc(clamped * FOCUS_FACTOR) / FOCUS_FACTOR;
}

/** contain 이미지의 실제 여백 방향에 맞는 정렬 축만 반환한다. */
export function getFormationFitAlignmentAxis(
  containerWidth: number,
  containerHeight: number,
  imageWidth: number,
  imageHeight: number,
): FormationFitAlignmentAxis {
  if (
    containerWidth <= 0 ||
    containerHeight <= 0 ||
    imageWidth <= 0 ||
    imageHeight <= 0
  ) {
    return 'none';
  }

  const containerRatio = containerWidth / containerHeight;
  const imageRatio = imageWidth / imageHeight;
  const relativeDifference = Math.abs(imageRatio / containerRatio - 1);

  if (relativeDifference <= ASPECT_RATIO_EPSILON) {
    return 'none';
  }

  return imageRatio < containerRatio ? 'horizontal' : 'vertical';
}

export function getFormationContainImageLayout(
  containerWidth: number,
  containerHeight: number,
  imageWidth: number,
  imageHeight: number,
): FormationContainImageLayout | null {
  if (
    containerWidth <= 0 ||
    containerHeight <= 0 ||
    imageWidth <= 0 ||
    imageHeight <= 0
  ) {
    return null;
  }

  const scale = Math.min(
    containerWidth / imageWidth,
    containerHeight / imageHeight,
  );
  const scaledWidth = imageWidth * scale;
  const scaledHeight = imageHeight * scale;

  return {
    scaledWidth,
    scaledHeight,
    whitespaceX: Math.max(0, containerWidth - scaledWidth),
    whitespaceY: Math.max(0, containerHeight - scaledHeight),
  };
}

export function isPointInsideFormationContainImage(
  pointX: number,
  pointY: number,
  layout: FormationContainImageLayout,
  positionX: number,
  positionY: number,
): boolean {
  const left = layout.whitespaceX * positionX;
  const top = layout.whitespaceY * positionY;

  return (
    pointX >= left &&
    pointX <= left + layout.scaledWidth &&
    pointY >= top &&
    pointY <= top + layout.scaledHeight
  );
}

export function applyFormationFitDragDelta(
  startPosition: number,
  delta: number,
  whitespace: number,
): number {
  return whitespace > 0 ? startPosition + delta / whitespace : startPosition;
}

export function snapFormationFitPosition(position: number): number {
  if (position < 0.25) return 0;
  if (position > 0.75) return 1;
  return 0.5;
}

export function getFormationHorizontalAlignmentPosition(
  alignment: FormationHorizontalAlignment,
): number {
  return {
    [FormationHorizontalAlignment.LEFT]: 0,
    [FormationHorizontalAlignment.CENTER]: 0.5,
    [FormationHorizontalAlignment.RIGHT]: 1,
  }[alignment];
}

export function getFormationVerticalAlignmentPosition(
  alignment: FormationVerticalAlignment,
): number {
  return {
    [FormationVerticalAlignment.TOP]: 0,
    [FormationVerticalAlignment.CENTER]: 0.5,
    [FormationVerticalAlignment.BOTTOM]: 1,
  }[alignment];
}

export function getFormationHorizontalAlignmentFromPosition(
  position: number,
): FormationHorizontalAlignment {
  if (position === 0) return FormationHorizontalAlignment.LEFT;
  if (position === 1) return FormationHorizontalAlignment.RIGHT;
  return FormationHorizontalAlignment.CENTER;
}

export function getFormationVerticalAlignmentFromPosition(
  position: number,
): FormationVerticalAlignment {
  if (position === 0) return FormationVerticalAlignment.TOP;
  if (position === 1) return FormationVerticalAlignment.BOTTOM;
  return FormationVerticalAlignment.CENTER;
}

function isPendingFormationItemId(id: string | number): boolean {
  return typeof id === 'string' && id.startsWith(PENDING_FORMATION_ITEM_ID_PREFIX);
}

/** 저장된 구형 Formation은 기존 cover 동작을 유지한다. */
export function getFormationImageMode(item: FormationItemData): FormationImageMode {
  return item.imageMode ?? FormationImageMode.FILL;
}

export function getFormationHorizontalAlignment(
  item: FormationItemData,
): FormationHorizontalAlignment {
  return item.horizontalAlignment ?? FormationHorizontalAlignment.CENTER;
}

export function getFormationVerticalAlignment(
  item: FormationItemData,
): FormationVerticalAlignment {
  return item.verticalAlignment ?? FormationVerticalAlignment.CENTER;
}

export function getFormationImagePosition(item: FormationItemData): {
  xPercent: number;
  yPercent: number;
} {
  if (getFormationImageMode(item) === FormationImageMode.FILL) {
    return {
      xPercent: normalizeFormationFocus(item.focusX) * 100,
      yPercent: normalizeFormationFocus(item.focusY) * 100,
    };
  }

  return {
    xPercent: getFormationHorizontalAlignmentPosition(
      getFormationHorizontalAlignment(item),
    ) * 100,
    yPercent: getFormationVerticalAlignmentPosition(
      getFormationVerticalAlignment(item),
    ) * 100,
  };
}

/** 슬롯 안에서 실제로 보이는 이미지의 하단 좌표를 반환한다. */
export function getFormationVisibleImageBottom(
  item: FormationItemData,
  slotTop: number,
  slotWidth: number,
  slotHeight: number,
): number | null {
  if (slotWidth <= 0 || slotHeight <= 0) return null;

  const resolved = resolveFormationSlotImage(item);
  if (!resolved) return null;

  const slotBottom = slotTop + slotHeight;
  if (getFormationImageMode(item) === FormationImageMode.FILL) {
    return slotBottom;
  }

  const layout = getFormationContainImageLayout(
    slotWidth,
    slotHeight,
    resolved.width,
    resolved.height,
  );
  if (!layout) return slotBottom;

  const { yPercent } = getFormationImagePosition(item);
  return slotTop + layout.whitespaceY * (yPercent / 100) + layout.scaledHeight;
}

/** 슬롯 안에서 실제로 보이는 이미지의 오른쪽 좌표를 반환한다. */
export function getFormationVisibleImageRight(
  item: FormationItemData,
  slotLeft: number,
  slotWidth: number,
  slotHeight: number,
): number | null {
  if (slotWidth <= 0 || slotHeight <= 0) return null;

  const resolved = resolveFormationSlotImage(item);
  if (!resolved) return null;

  const slotRight = slotLeft + slotWidth;
  if (getFormationImageMode(item) === FormationImageMode.FILL) {
    return slotRight;
  }

  const layout = getFormationContainImageLayout(
    slotWidth,
    slotHeight,
    resolved.width,
    resolved.height,
  );
  if (!layout) return slotRight;

  const { xPercent } = getFormationImagePosition(item);
  return slotLeft + layout.whitespaceX * (xPercent / 100) + layout.scaledWidth;
}

function isFormationDirectUploadItem(item: FormationItemData): boolean {
  return getFormationItemSource(item) === 'directUpload';
}

function buildUploadFileSnapshot(
  originUrl: string,
  width = 0,
  height = 0,
) {
  return {
    id: '',
    originUrl,
    allowDownload: false,
    width,
    height,
  };
}

export function buildFormationSlotWorkSnapshot(
  selected: SelectedData,
  work?: WorkSimpleWithDetails,
): PEWorkData {
  const uploadFile = work?.uploadFile;
  return {
    id: selected.workId,
    workType: work?.workType ?? WorkType.PAINTING,
    uploadFile: buildUploadFileSnapshot(
      selected.originUrl,
      uploadFile?.width ?? 0,
      uploadFile?.height ?? 0,
    ),
    title: selected.title ?? work?.title ?? '',
    titleEn: work?.titleEn ?? '',
    description: '',
    descriptionEn: '',
    size: selected.size ?? work?.size ?? getDefaultSizeData(),
    sizeText: selected.sizeText ?? work?.sizeText ?? '',
    dimensionsType: selected.dimensionsType ?? work?.dimensionsType ?? DimensionsType.NONE,
    material: selected.material ?? work?.material ?? '',
    materialEn: work?.materialEn ?? '',
    prodYear: selected.prodYear ?? work?.prodYear ?? '',
    videoUrl: '',
    duration: selected.duration ?? work?.duration ?? '',
    artist: selected.artist ?? work?.artist ?? null,
  };
}

export function buildFormationSlotWorkDetailSnapshot(
  selected: SelectedData,
  detail?: WorkDetailSimpleData,
): WorkDetailData {
  const uploadFile = detail?.uploadFile;
  return {
    id: selected.detailId!,
    workId: selected.workId,
    uploadFile: buildUploadFileSnapshot(
      selected.originUrl,
      uploadFile?.width ?? 0,
      uploadFile?.height ?? 0,
    ),
    description: detail?.description ?? '',
    detailType: detail?.detailType ?? DetailType.IMAGE,
    videoUrl: '',
  };
}

export function convertChangedFormationItemToUpdateReq(
  item: FormationItemData,
): UpdateFormationItemReq {
  const identity = isPendingFormationItemId(item.id) ? {} : { id: item.id };
  const presentation = {
    slotIndex: item.slotIndex,
    focusX: normalizeFormationFocus(item.focusX),
    focusY: normalizeFormationFocus(item.focusY),
    imageMode: getFormationImageMode(item),
    horizontalAlignment: getFormationHorizontalAlignment(item),
    verticalAlignment: getFormationVerticalAlignment(item),
  };
  const directUploadCaption = isFormationDirectUploadItem(item)
    ? { caption: item.caption ?? '' }
    : {};

  if (item.referenceChanged) {
    if (item.workDetail?.id) {
      return { ...identity, workDetailId: item.workDetail.id, caption: '', ...presentation };
    }
    if (item.work?.id) {
      return { ...identity, workId: item.work.id, caption: '', ...presentation };
    }
  }

  if (item.imageChanged && isFormationDirectUploadItem(item)) {
    return {
      ...identity,
      updateUploadFileReq: {
        uploadFileId: item.uploadFile.id,
        originUrl: item.uploadFile.originUrl,
        allowDownload: item.uploadFile.allowDownload,
        width: item.uploadFile.width,
        height: item.uploadFile.height,
      },
      ...directUploadCaption,
      ...presentation,
    };
  }

  return {
    ...identity,
    ...directUploadCaption,
    ...presentation,
  };
}

function getFormationItemCount(formationType: FormationType): number {
  switch (normalizeFormationType(formationType)) {
    case FormationType.TWO_COLUMNS:
    case FormationType.FEATURE_SIDE:
      return 2;
    case FormationType.THREE_COLUMNS:
      return 3;
    default:
      return 2;
  }
}

function createEmptyFormationItem(slotIndex: number): FormationItemData {
  return {
    id: `${PENDING_FORMATION_ITEM_ID_PREFIX}${slotIndex}`,
    slotIndex,
    work: null,
    workDetail: null,
    uploadFile: {
      id: '',
      originUrl: '',
      allowDownload: false,
      width: 0,
      height: 0,
    },
    caption: '',
    focusX: 0.5,
    focusY: 0.5,
    imageMode: FormationImageMode.FIT,
    horizontalAlignment: FormationHorizontalAlignment.CENTER,
    verticalAlignment: FormationVerticalAlignment.CENTER,
  };
}

/** 백엔드 Formation.changeFormationType과 동일한 슬롯 수 조정 */
export function adjustFormationItemsForType(
  items: FormationItemData[],
  formationType: FormationType,
): FormationItemData[] {
  const newCount = getFormationItemCount(formationType);
  let nextItems = [...items].sort((a, b) => a.slotIndex - b.slotIndex);

  if (newCount > nextItems.length) {
    for (let i = nextItems.length; i < newCount; i++) {
      nextItems.push(createEmptyFormationItem(i));
    }
  } else if (newCount < nextItems.length) {
    nextItems = nextItems.filter((item) => item.slotIndex < newCount);
  }

  return nextItems.map((item, index) => ({
    ...item,
    slotIndex: index,
  }));
}
